import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import BottomNav from '@/components/BottomNav';
import CircleProgress from '@/components/CircleProgress';
import PointsHistory from '@/components/PointsHistory';
import RewardsLeaderboard from '@/components/RewardsLeaderboard';
import CircleTiers from '@/components/CircleTiers';
import HowToEarn from '@/components/HowToEarn';
import TopInviters from '@/components/TopInviters';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { ArrowLeft, Shield, Coins, Trophy, Layers, HelpCircle, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Rewards = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [profile, setProfile] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [taskTypes, setTaskTypes] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('progress');

  useEffect(() => {
    const headers = getAuthHeaders();
    Promise.all([
      axios.post(`${API_URL}/api/rewards/check-daily-login`, {}, { headers }),
      axios.get(`${API_URL}/api/rewards/profile`, { headers }),
      axios.get(`${API_URL}/api/rewards/leaderboard`, { headers }),
      axios.get(`${API_URL}/api/rewards/task-types`, { headers }),
      axios.get(`${API_URL}/api/rewards/history`, { headers }),
    ])
      .then(([login, prof, lb, tasks, hist]) => {
        if (login.data.daily_awarded) {
          toast.success(`+${login.data.points_earned} pts for daily login!`);
        }
        if (login.data.leveled_up) {
          toast.success('Circle promotion! Your credibility grew!');
        }
        setProfile(prof.data);
        setLeaderboard(lb.data);
        setTaskTypes(tasks.data.categories);
        setHistory(hist.data.transactions);
      })
      .catch(() => toast.error('Failed to load profile'))
      .finally(() => setLoading(false));
  }, []);

  const tabs = [
    { id: 'progress', label: 'Credibility', icon: Shield },
    { id: 'history', label: 'Activity', icon: Coins },
    { id: 'leaderboard', label: 'Trust Ranks', icon: Trophy },
    { id: 'inviters', label: 'Top Inviters', icon: Sparkles },
    { id: 'circles', label: 'Circles', icon: Layers },
    { id: 'earn', label: 'How to Grow', icon: HelpCircle },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 pb-24">
        <div className="app-container px-4 pt-6 space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-40 rounded-2xl" />
          <Skeleton className="h-32 rounded-2xl" />
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="rewards-page">
      <div className="app-container px-4 pt-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <button onClick={() => navigate(-1)} className="p-2 rounded-full bg-white shadow-sm" data-testid="rewards-back-btn">
            <ArrowLeft size={20} className="text-slate-700" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-slate-900">Financial Profile</h1>
            <p className="text-xs text-slate-500">Trust, credibility, and financial discipline</p>
          </div>
          <Card className="glass-card px-3 py-1.5">
            <p className="text-lg font-black text-[#0B1F3A]" data-testid="credibility-score-display">
              {profile?.credibility_score || 0}
            </p>
            <p className="text-[9px] text-slate-500 text-center">CREDIBILITY</p>
          </Card>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-1.5 mb-5 overflow-x-auto pb-1 no-scrollbar">
          {tabs.map((t) => (
            <Button
              key={t.id}
              size="sm"
              variant={tab === t.id ? 'default' : 'outline'}
              className={`flex-shrink-0 text-[11px] rounded-xl gap-1.5 ${
                tab === t.id ? 'bg-[#0B1F3A] text-white' : 'text-[#0B1F3A]'
              }`}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
            >
              <t.icon size={13} />
              {t.label}
            </Button>
          ))}
        </div>

        {/* Tab Content */}
        {tab === 'progress' && <CircleProgress profile={profile} />}
        {tab === 'history' && <PointsHistory transactions={history} />}
        {tab === 'leaderboard' && (
          <RewardsLeaderboard
            leaders={leaderboard?.leaders}
            myRank={leaderboard?.my_rank}
            myScore={leaderboard?.my_score}
          />
        )}
        {tab === 'inviters' && <TopInviters />}
        {tab === 'circles' && (
          <CircleTiers
            circles={profile?.all_circles}
            currentCircle={profile?.current_circle}
            totalPoints={profile?.credibility_score || 0}
          />
        )}
        {tab === 'earn' && <HowToEarn categories={taskTypes} />}
      </div>
      <BottomNav />
    </div>
  );
};

export default Rewards;
